import {
  Component,
  ErrorHandler,
  HostBinding,
  OnInit,
  inject,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { JsonValue } from '@bufbuild/protobuf';
import { fromJson } from '@bufbuild/protobuf';
import { ConnectError } from '@connectrpc/connect';
import { NbToastrService, NbWindowRef } from '@nebular/theme';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormFieldSelectAutocomplete } from '@vmspdb/components/form-fields/select-autocomplete';
import {
  TimeTrackingServiceCreateRequestJson,
  TimeTrackingServiceCreateRequestSchema,
  TimeTrackingServiceCreateResponse,
} from '@vmspdb/definitions';
import { selectAuthUserId } from '@vmspdb/ui/store';
import { first, tap } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TimeTrackingRepositoryService } from '../../../repositories';
import { VOID } from '../../../shared/utils';
import { selectMyTimeTracking } from '../../../store/time-tracking';
import { selectAllEmployeeOptionsWithSelected } from '../../../store/users';

@UntilDestroy()
@Component({
  selector: 'vmspdb-gllobal-time-tracking-office-modal',
  templateUrl: './global-time-tracking-office-modal.component.html',
  styleUrl: './global-time-tracking-office-modal.component.scss',
  standalone: false,
})
export class GlobalTimeTrackingOfficeModalComponent implements OnInit {
  @HostBinding('attr.data-testid')
  testid = 'modal-global-time-tracking-office';

  private store = inject(Store);
  private repository = inject(TimeTrackingRepositoryService);
  private nbWindowRef = inject(NbWindowRef);
  protected errorHandler = inject(ErrorHandler);
  protected toastr = inject(NbToastrService);

  errors: string[] = [];

  form = new FormGroup({});

  loaded = false;

  modelName = 'Time Tracking Entry';
  myTracking$ = this.store.select(selectMyTimeTracking);

  model: any = {
    flags: {
      nonProject: true,
    },
  };

  fields: FormlyFieldConfig[];
  processing = false;

  constructor() {
    this.fields = this.getFieldConfig();
  }

  ngOnInit() {
    this.fields = this.getFieldConfig();
    this.store
      .select(selectAuthUserId)
      .pipe(
        untilDestroyed(this),
        tap(
          (employeeUuid) =>
            (this.model = this.model.uuid
              ? { ...this.model }
              : { ...this.model, employeeUuid }),
        ),
      )
      .subscribe();
  }

  private getFieldConfig(): FormlyFieldConfig[] {
    const employee$ = selectAllEmployeeOptionsWithSelected(
      this.model.employeeUuid ?? '',
    );

    return [
      {
        key: 'employeeUuid',
        type: FormFieldSelectAutocomplete,
        props: {
          label: 'Employee',
          required: true,
          options: this.store.select(employee$),
          disabled: true,
        },
      },
      {
        key: 'notes',
        type: 'textarea',
        props: {
          label: 'Notes',
          required: false,
          rows: 6,
        },
      },
    ];
  }

  saveChanges() {
    this.processing = true;
    this.errors = [];
    const model: TimeTrackingServiceCreateRequestJson = {
      ...this.model,
      inProgress: true,
      startTime: new Date().toISOString(),
      date: new Date().toISOString(),
    };

    return this.store.select(selectAuthUserId).pipe(
      first(),
      switchMap((employeeUuid) =>
        this.repository.create(
          fromJson(
            TimeTrackingServiceCreateRequestSchema,
            {
              ...model,
              employeeUuid,
            } as JsonValue,
            {
              ignoreUnknownFields: true,
            },
          ),
        ),
      ),
      tap(() => this.toastr.success(`Time tracking started!`, 'Success')),
      untilDestroyed(this),
      tap(() => this.nbWindowRef.close()),
      catchError((err: ConnectError) => {
        this.errorHandler.handleError(err);
        this.errors = [err.message];
        return VOID;
      }),
      tap(() => (this.processing = false)),
    );
  }

  finishTracking() {
    this.processing = true;
    return this.myTracking$.pipe(
      first(),
      untilDestroyed(this),
      map((res) => res as unknown as TimeTrackingServiceCreateResponse),
      switchMap((model: TimeTrackingServiceCreateResponse) => {
        return this.repository
          .markComplete(model.uuid)
          .pipe(untilDestroyed(this));
      }),
      tap(() => this.toastr.success(`Time tracking completed!`, 'Success')),
      tap(() => this.nbWindowRef.close()),
      catchError((err) => {
        this.errorHandler.handleError(err);
        this.toastr.danger('There was an error saving your changes', 'Error');
        return VOID;
      }),
      tap(() => (this.processing = false)),
    );
  }

  submit() {
    if (this.model.uuid) {
      this.finishTracking().subscribe();
      return;
    }

    this.saveChanges().subscribe();
  }
}
